import React from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FREE_BLOCK_LABELS = {
  recognition: "Recognition",
  distortion: "Distortion",
  activation: "Activation",
  real_life_expression: "Real-Life Expression",
  spirit_animal: "Spirit Animal",
  soul_truths: "Soul Truths",
};

const PLACEMENT_LABEL = {
  sun: "Sun",
  moon: "Moon",
  mercury: "Mercury",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  chiron: "Chiron",
  uranus: "Uranus",
  neptune: "Neptune",
  pluto: "Pluto",
  north_node: "Direction",
  south_node: "Origin",
  ascendant: "Rising",
  midheaven: "Crown",
};

const labelOf = (cp) => {
  if (!cp) return "";
  const key = String(cp).toLowerCase().replace(/\s+/g, "_");
  return PLACEMENT_LABEL[key] || cp;
};

const PORTAL_DIVIDER =
  "https://static.prod-images.emergentagent.com/jobs/12ae9d75-2045-46a7-a3b7-9b9136f324b8/images/0978221e95672a33af1d1baee8b85a92566c02476b368ecaf1405c7f8a2a486b.png";

const VELVET_BG =
  "https://static.prod-images.emergentagent.com/jobs/12ae9d75-2045-46a7-a3b7-9b9136f324b8/images/c0b4f37abec352d74430959ca0360ac83cb4258abe85540adfaae658a978a4cb.png";

/* Renders block text — supports multi-paragraph content (split on blank line). */
const BlockText = ({ text, className, testid }) => {
  if (!text) return null;
  const paragraphs = String(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length <= 1) {
    return (
      <p className={className} data-testid={testid}>
        {text}
      </p>
    );
  }
  return (
    <div className="space-y-4" data-testid={testid}>
      {paragraphs.map((p, i) => (
        <p key={i} className={className}>
          {p}
        </p>
      ))}
    </div>
  );
};

const PortalDivider = ({ label }) => (
  <div className="my-16 md:my-24 flex flex-col items-center text-center px-6">
    <img
      src={PORTAL_DIVIDER}
      alt=""
      className="w-48 md:w-64 opacity-70"
      style={{ mixBlendMode: "screen" }}
    />
    {label && <div className="overline mt-6">{label}</div>}
  </div>
);

/* ---------- Free expanded section (full prose for fields 1-4) ---------- */
const ExpandedFreeSection = ({ section, index }) => {
  const placement = section.placements?.[0];
  const primary = section.primary_keycode;
  const blocks = section.blocks_order || Object.keys(section.blocks || {});

  return (
    <article
      className="relative py-20 md:py-28"
      data-testid={`section-${section.field_id}`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
        {/* Left column — number + keycode + meta */}
        <div className="md:col-span-4 md:sticky md:top-24 self-start space-y-6">
          <div className="overline">{`Field ${section.field_number} of 11`}</div>
          <div className="font-display text-7xl md:text-8xl italic leading-none text-[var(--sf-leopard-gold)] opacity-60">
            {String(section.field_number).padStart(2, "0")}
          </div>
          {primary && (
            <div className="space-y-2">
              <div className="overline">Primary Keycode</div>
              <div className="font-display text-3xl md:text-4xl text-[var(--sf-ivory)]">
                {primary.animal}
              </div>
              <div className="font-display italic text-lg text-[var(--sf-text-secondary)]">
                {primary.archetype_name}
              </div>
              <div className="text-xs uppercase tracking-widest text-[var(--sf-text-muted)] pt-2">
                Keycode #{primary.id} · {primary.gate_origin}
              </div>
            </div>
          )}
          {placement && (
            <div className="pt-4 border-t border-[var(--sf-border)] space-y-1">
              <div className="text-sm text-[var(--sf-text-secondary)]">
                <span className="text-[var(--sf-leopard-gold)]">{labelOf(placement.label)}</span>{" "}
                in <span className="italic">{placement.sign}</span>
                {placement.house ? `, House ${placement.house}` : ""}
              </div>
              {placement.arena_name && (
                <div className="text-xs uppercase tracking-widest text-[var(--sf-text-muted)]">
                  {placement.arena_name}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right column — content */}
        <div className="md:col-span-8 relative">
          <div className="keycode-aura absolute -top-16 -right-10" />
          <div className="overline mb-3">{section.subtitle}</div>
          <h2 className="font-display text-4xl md:text-6xl leading-tight mb-6 text-[var(--sf-ivory)]">
            {section.title}
          </h2>
          <p className="codex-emphasis mb-10">{section.opening_line}</p>

          <div className="space-y-10">
            {blocks.map((b) => {
              const text = section.blocks?.[b];
              if (!text) return null;

              if (b === "soul_truths") {
                return (
                  <div
                    key={b}
                    className="pt-6 border-t border-[var(--sf-border)]"
                  >
                    <div className="overline mb-3">{FREE_BLOCK_LABELS[b]}</div>
                    <BlockText
                      text={text}
                      className="codex-emphasis"
                      testid={`block-${section.field_id}-${b}`}
                    />
                  </div>
                );
              }

              return (
                <div key={b}>
                  <div className="overline mb-3">{FREE_BLOCK_LABELS[b] || b}</div>
                  <BlockText
                    text={text}
                    className="codex-text"
                    testid={`block-${section.field_id}-${b}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {index < 3 && (
        <div className="field-divider mt-12">
          <span className="line" />
          <span className="symbol" />
          <span className="line" />
        </div>
      )}
    </article>
  );
};

/* ---------- Locked teaser card (fields 5-11 in the free Codex) ---------- */
const LockedTeaserCard = ({ section, onUnlockClick }) => {
  const blocks = section.blocks || {};
  return (
    <article
      className="sf-card p-8 md:p-10 relative h-full flex flex-col"
      data-testid={`section-${section.field_id}`}
    >
      <div className="overline mb-3">{`Field ${section.field_number} · Locked`}</div>
      <h3 className="font-display text-2xl md:text-3xl mb-2 text-[var(--sf-ivory)]">
        {blocks.teaser_title || section.title}
      </h3>
      <div className="text-xs uppercase tracking-widest text-[var(--sf-leopard-gold)] mb-5">
        {section.subtitle}
      </div>
      <p
        className="codex-text mb-6 flex-1"
        data-testid={`block-${section.field_id}-teaser`}
      >
        {blocks.teaser}
      </p>
      <button
        type="button"
        onClick={onUnlockClick}
        className="pt-4 mt-2 border-t border-[var(--sf-border)] flex items-center gap-2 text-left group transition-opacity hover:opacity-80 print:hidden"
        data-testid={`unlock-jump-${section.field_id}`}
      >
        <span className="font-display italic text-base text-[var(--sf-leopard-gold)] group-hover:underline underline-offset-4 decoration-[var(--sf-leopard-gold)]/50">
          {blocks.unlock_line}
        </span>
        <span
          aria-hidden
          className="text-[var(--sf-leopard-gold)] transition-transform group-hover:translate-x-1"
        >
          →
        </span>
      </button>
    </article>
  );
};

/* ---------- Intro for the free reading ---------- */
const ResultIntro = () => (
  <section
    className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16"
    data-testid="result-intro"
  >
    <div className="sf-card p-10 md:p-14">
      <div className="overline mb-6 text-center">How To Read This Codex</div>
      <div className="space-y-5">
        <p className="codex-text">
          This Codex is built through energetic keycodes — archetypal
          frequencies translated as animal intelligence. They are not symbols.
          They are behavioural patterns.
        </p>
        <p className="codex-text">
          Across eleven fields, your design becomes visible. The first four are
          revealed in full depth — the core mechanics of your identity, emotion,
          attraction, and power. The remaining seven are previewed here. They
          unlock together with the Full Codex.
        </p>
        <p className="codex-emphasis pt-2">
          Read slowly. You are meant to recognise yourself.
        </p>
      </div>
    </div>
  </section>
);

/* ---------- Closing CTA on the free reading ---------- */
const FreeUnlockCTA = ({ onUnlock }) => {
  const [showCoupon, setShowCoupon] = React.useState(false);
  const [coupon, setCoupon] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleUnlock = async (couponCode) => {
    setSubmitting(true);
    try {
      await onUnlock(couponCode || undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <section
    id="unlock-cta"
    className="relative max-w-5xl mx-auto px-6 md:px-12 my-20 scroll-mt-24"
    data-testid="ending-section"
  >
    <div className="relative rounded-2xl overflow-hidden p-10 md:p-16 border border-[var(--sf-border-strong)]">
      <img
        src={VELVET_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-50"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B0C10]/40 via-[#4A0E4E]/40 to-[#0B0C10]/75" />
      <div className="relative space-y-5">
        <div className="overline mb-3">The Entry Layer</div>
        <p className="codex-text">
          You have read the entry layer of your Codex — four fields revealed in
          full depth, seven previewed.
        </p>
        <p className="codex-text">
          The Full Codex is the complete, book-length translation of your soul
          design. All eleven fields written out across applied realms — Life,
          Love, Leadership, and Leisure — and delivered as a beautifully
          formatted PDF you can keep, print, and return to.
        </p>
        <p className="codex-emphasis text-2xl pt-3">
          If this already feels accurate — you are only seeing the surface
          structure.
        </p>
        <div className="pt-4 print:hidden flex flex-wrap items-center gap-5">
          <button
            className="sf-btn-primary"
            onClick={() => handleUnlock("")}
            disabled={submitting}
            data-testid="unlock-full-codex-button"
          >
            {submitting ? "Opening checkout…" : "Unlock Full Codex · $77"}
          </button>
          <span className="text-xs tracking-widest uppercase text-[var(--sf-text-muted)]">
            One-time · all 11 fields delivered by email
          </span>
        </div>

        {/* Coupon code — collapsed link, expands inline */}
        <div className="pt-3 print:hidden">
          {!showCoupon ? (
            <button
              type="button"
              onClick={() => setShowCoupon(true)}
              className="text-xs tracking-widest uppercase text-[var(--sf-text-muted)] hover:text-[var(--sf-leopard-gold)] transition-colors underline-offset-4 hover:underline"
              data-testid="show-coupon-toggle"
            >
              Have a coupon code?
            </button>
          ) : (
            <div
              className="flex flex-wrap items-center gap-3 max-w-md"
              data-testid="coupon-form"
            >
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                placeholder="COUPON CODE"
                className="flex-1 min-w-[180px] px-4 py-2.5 bg-[rgba(11,12,16,0.6)] border border-[var(--sf-border)] rounded-md text-[var(--sf-ivory)] placeholder:text-[var(--sf-text-muted)] tracking-widest text-sm focus:outline-none focus:border-[var(--sf-leopard-gold)]"
                data-testid="coupon-input"
                autoFocus
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => handleUnlock(coupon)}
                disabled={!coupon.trim() || submitting}
                className="sf-btn-ghost"
                data-testid="apply-coupon-button"
              >
                {submitting ? "Applying…" : "Apply & Unlock"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </section>
  );
};

/* ---------- Paid view — clean download panel only ---------- */
const PaidDownloadPanel = ({ readingId, mainPlacements, expansionStatus, onRetry }) => {
  const pdfUrl = `${BACKEND_URL}/api/reading/${readingId}/pdf`;
  const isPartial = expansionStatus === "partial";
  return (
    <section
      className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28"
      data-testid="paid-download-panel"
    >
      <div className="text-center mb-10">
        <div
          className="inline-block px-5 py-1.5 mb-6 rounded-full border border-[var(--sf-leopard-gold)] text-xs tracking-[0.3em] uppercase text-[var(--sf-leopard-gold)]"
          data-testid="full-codex-badge"
        >
          {isPartial ? "Full Codex · Partial" : "Full Codex · Unlocked"}
        </div>
        <div className="overline mb-5">Your Soul Femme Codex</div>
        <h1 className="font-display text-5xl md:text-6xl mb-6 leading-tight text-[var(--sf-ivory)]">
          Your Codex is{" "}
          <span className="italic" style={{ color: "var(--sf-leopard-gold)" }}>
            ready
          </span>
        </h1>
      </div>

      <div className="relative rounded-2xl overflow-hidden p-10 md:p-16 border border-[var(--sf-border-strong)]">
        <img
          src={VELVET_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B0C10]/55 via-[#4A0E4E]/45 to-[#0B0C10]/80" />
        <div className="relative text-center space-y-6">
          <p className="codex-text max-w-xl mx-auto">
            Your Full Codex has been written out across all eleven fields and
            four applied realms. It is too much to live on a web page — it is
            meant to be held, read slowly, and returned to.
          </p>
          <p className="codex-emphasis max-w-xl mx-auto">
            Download your private PDF below. It is yours to keep.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4 print:hidden">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sf-btn-primary"
              data-testid="download-full-codex-pdf-button"
            >
              Download Full Codex PDF
            </a>
          </div>
          <p className="text-xs uppercase tracking-widest text-[var(--sf-text-muted)] pt-2">
            One-time download · approx. 30+ pages · A4 print-ready
          </p>
        </div>
      </div>

      {isPartial && (
        <div
          className="mt-8 p-6 rounded-xl border border-[var(--sf-leopard-gold)]/40 bg-[rgba(212,175,55,0.05)]"
          data-testid="partial-notice"
        >
          <div className="overline mb-2 text-[var(--sf-leopard-gold)]">
            A few fields didn't fully render
          </div>
          <p className="codex-text mb-4">
            The AI was running slow when your Codex was woven, so a few fields
            may appear briefly. Tap below to re-weave them — your PDF will
            update automatically.
          </p>
          <button
            type="button"
            className="sf-btn-ghost"
            onClick={onRetry}
            data-testid="retry-expansion-button"
          >
            Re-weave missing fields
          </button>
        </div>
      )}

      {mainPlacements && Object.keys(mainPlacements).length > 0 && (
        <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm tracking-widest uppercase text-[var(--sf-text-muted)]">
          {Object.entries(mainPlacements).map(([k, v]) => (
            <span key={k} data-testid={`placement-${k}`}>
              {labelOf(k)}{" "}
              <span className="text-[var(--sf-leopard-gold)]">{v.sign}</span>
              {v.house ? ` · H${v.house}` : ""}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

/* ---------- Main component ---------- */
export const Reading = ({ reading, onReset, onUnlock, onRetryExpansion }) => {
  if (!reading) return null;
  const expanded = !!reading.expanded;

  // PAID — show only the download panel.
  if (expanded) {
    return (
      <div data-testid="reading-container" className="pb-32">
        <PaidDownloadPanel
          readingId={reading.id}
          mainPlacements={reading.main_placements}
          expansionStatus={reading.expansion_status}
          onRetry={onRetryExpansion}
        />
        <div className="text-center pb-8 print:hidden">
          <button
            className="sf-btn-ghost"
            onClick={onReset}
            data-testid="start-over-button"
          >
            Begin Another Reading
          </button>
        </div>
      </div>
    );
  }

  // FREE — 4 expanded fields, 7 locked teasers, single unlock CTA.
  const sections = reading.sections || [];
  const expandedSections = sections.filter((s) => !s.locked);
  const lockedSections = sections.filter((s) => s.locked);

  const scrollToUnlock = () => {
    const el = document.getElementById("unlock-cta");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // brief highlight pulse on the primary button
      const btn = el.querySelector('[data-testid="unlock-full-codex-button"]');
      if (btn) {
        btn.classList.add("sf-btn-pulse");
        setTimeout(() => btn.classList.remove("sf-btn-pulse"), 1600);
      }
    }
  };

  return (
    <div data-testid="reading-container" className="pb-32">
      {/* Masthead */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10 text-center">
        <div className="overline mb-5">Your Soul Femme Codex</div>
        <h1 className="font-display text-5xl md:text-7xl mb-6 leading-tight">
          The pattern is{" "}
          <span className="italic" style={{ color: "var(--sf-leopard-gold)" }}>
            visible
          </span>
        </h1>
        <p className="codex-text max-w-2xl mx-auto mb-8">
          Eleven fields. Four written out in full, seven previewed. Read slowly
          — the Codex is designed to be felt as much as understood.
        </p>
        {reading.main_placements && (
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm tracking-widest uppercase text-[var(--sf-text-muted)]">
            {Object.entries(reading.main_placements).map(([k, v]) => (
              <span key={k} data-testid={`placement-${k}`}>
                {labelOf(k)}{" "}
                <span className="text-[var(--sf-leopard-gold)]">{v.sign}</span>
                {v.house ? ` · H${v.house}` : ""}
              </span>
            ))}
          </div>
        )}
      </section>

      <PortalDivider label="The Threshold" />

      <ResultIntro />

      <PortalDivider label="Your Four Core Fields" />

      <div data-testid="expanded-sections">
        {expandedSections.map((s, i) => (
          <ExpandedFreeSection key={s.field_id} section={s} index={i} />
        ))}
      </div>

      {lockedSections.length > 0 && (
        <>
          <PortalDivider label="Locked · Awaiting the Full Codex" />
          <section className="max-w-7xl mx-auto px-6 md:px-12">
            <div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              data-testid="locked-grid"
            >
              {lockedSections.map((s) => (
                <LockedTeaserCard
                  key={s.field_id}
                  section={s}
                  onUnlockClick={scrollToUnlock}
                />
              ))}
            </div>
          </section>
        </>
      )}

      <FreeUnlockCTA onUnlock={onUnlock} />

      <div className="text-center pb-8 print:hidden">
        <button
          className="sf-btn-ghost"
          onClick={onReset}
          data-testid="start-over-button"
        >
          Begin Another Reading
        </button>
      </div>
    </div>
  );
};

export default Reading;
